import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { formatAmountInput, parseAmountInput } from "../lib/money.js";
import { cn } from "../lib/utils.js";
import { inputBaseClass } from "./ui/input.js";
/** Quita una coma decimal pendiente: `"1500."` → `"1500"`. */
function toCanonical(draft) {
    return draft.endsWith('.') ? draft.slice(0, -1) : draft;
}
function countSignificant(text, allowDecimals) {
    let count = 0;
    for (const ch of text)
        if ((ch >= '0' && ch <= '9') || (allowDecimals && ch === ','))
            count++;
    return count;
}
/** Posición en `display` tras `significant` caracteres significativos (dígitos y coma). */
function caretFor(display, significant, allowDecimals) {
    if (significant <= 0)
        return 0;
    let seen = 0;
    for (let i = 0; i < display.length; i++) {
        const ch = display.charAt(i);
        if ((ch >= '0' && ch <= '9') || (allowDecimals && ch === ','))
            seen++;
        if (seen === significant)
            return i + 1;
    }
    return display.length;
}
/**
 * Campo de monto en pesos con separador de miles mientras se escribe (`$ 1.200.000`).
 * Teclado numérico en celular; el valor sale como string decimal exacto.
 */
function AmountInput({ value, onValueChange, allowDecimals = false, maxIntegerDigits, invalid = false, size = 'md', className, containerClassName, ref, placeholder = '0', ...props }) {
    const inputRef = React.useRef(null);
    const pendingCaret = React.useRef(null);
    const [draft, setDraft] = React.useState(value);
    const [previousValue, setPreviousValue] = React.useState(value);
    // Si el valor cambia desde afuera, se adopta (conservando una coma pendiente propia).
    if (value !== previousValue) {
        setPreviousValue(value);
        if (value !== toCanonical(draft))
            setDraft(value);
    }
    React.useLayoutEffect(() => {
        const input = inputRef.current;
        if (pendingCaret.current === null || !input || input !== document.activeElement)
            return;
        input.setSelectionRange(pendingCaret.current, pendingCaret.current);
        pendingCaret.current = null;
    });
    const setRefs = React.useCallback((node) => {
        inputRef.current = node;
        if (typeof ref === 'function')
            ref(node);
        else if (ref)
            ref.current = node;
    }, [ref]);
    function handleChange(event) {
        let raw = event.target.value;
        let caret = event.target.selectionStart ?? raw.length;
        const options = { allowDecimals, maxIntegerDigits };
        let next = parseAmountInput(raw, options);
        // Se borró solo un separador de miles: borra también el dígito anterior, como espera la persona.
        if (next === draft && raw.length < formatAmountInput(draft).length && caret > 0) {
            const before = raw.slice(0, caret);
            const index = before.search(/\d(?=\D*$)/);
            if (index !== -1) {
                raw = raw.slice(0, index) + raw.slice(index + 1);
                caret = index;
                next = parseAmountInput(raw, options);
            }
        }
        const significant = countSignificant(raw.slice(0, caret), allowDecimals);
        pendingCaret.current = caretFor(formatAmountInput(next), significant, allowDecimals);
        setDraft(next);
        const canonical = toCanonical(next);
        if (canonical !== value)
            onValueChange(canonical);
    }
    return (_jsxs("div", { "data-slot": "amount-input", className: cn('relative w-full', containerClassName), children: [_jsx("span", { "aria-hidden": "true", className: cn('pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 font-display font-semibold text-muted-foreground', size === 'lg' ? 'text-3xl' : 'text-lg'), children: "$" }), _jsx("input", { ref: setRefs, type: "text", inputMode: allowDecimals ? 'decimal' : 'numeric', autoComplete: "off", spellCheck: false, placeholder: placeholder, "aria-invalid": invalid || undefined, ...props, value: formatAmountInput(draft), onChange: handleChange, className: cn(inputBaseClass, 'font-display font-semibold tabular-nums', size === 'lg' ? 'h-16 pl-11 text-3xl' : 'pl-9 text-lg', className) })] }));
}
export { AmountInput };
//# sourceMappingURL=amount-input.js.map